import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { categoryBySlug } from "../lib/categories";
import ProductCard from "../components/ProductCard";

export default function Category() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const meta = categoryBySlug(slug);

  useEffect(() => {
    api.get("/products", { params: { category: slug } }).then((r) => setProducts(r.data));
  }, [slug]);

  const sorted = [...products].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    if (sort === "name") return a.name.localeCompare(b.name);
    return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
  });

  if (!meta) {
    return (
      <div className="px-6 md:px-12 py-32 text-center">
        <h1 className="editorial-heading text-4xl text-secondary">Category not found</h1>
        <Link to="/" className="text-primary underline mt-4 inline-block">Return home</Link>
      </div>
    );
  }

  return (
    <div data-testid={`category-page-${slug}`} className="animate-in fade-in duration-500">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <img src={meta.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/30 to-transparent" />
        <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-16 md:pb-20 max-w-4xl">
          <p className="overline text-primary mb-5" data-testid="category-eyebrow">
            Research Category
          </p>
          <h1 className="editorial-heading text-5xl md:text-7xl lg:text-8xl text-background leading-[0.95] mb-6">
            {meta.name}
          </h1>
          <p className="text-background/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {meta.intro}
          </p>
        </div>
      </section>

      {/* TOOLBAR */}
      <section className="px-6 md:px-12 lg:px-24 pt-12 pb-6 flex items-center justify-between border-b border-border">
        <p className="text-sm text-muted-foreground" data-testid="category-product-count">
          {sorted.length} {sorted.length === 1 ? "product" : "products"}
        </p>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]">
          <span className="text-muted-foreground hidden md:inline">Sort</span>
          {[
            { v: "featured", l: "Featured" },
            { v: "price-asc", l: "Price ↑" },
            { v: "price-desc", l: "Price ↓" },
            { v: "name", l: "A–Z" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setSort(opt.v)}
              data-testid={`sort-${opt.v}`}
              className={`px-3 py-1.5 transition-colors ${
                sort === opt.v
                  ? "text-secondary border-b border-secondary"
                  : "text-muted-foreground hover:text-secondary"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        {sorted.length === 0 ? (
          <p className="text-center text-muted-foreground py-20">No products in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
            {sorted.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* SCIENCE */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-muted">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <p className="overline text-primary mb-4">The Science</p>
            <h2 className="editorial-heading text-4xl md:text-5xl text-secondary leading-[1.05]">
              Why this category matters.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {meta.science}
            </p>
            <p className="text-xs text-muted-foreground/80 mt-8 leading-relaxed">
              Disclaimer — all compounds shown are sold strictly for research and laboratory use.
              They are not approved for human consumption, treatment, or diagnosis of any
              condition.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
